import { Box, Button, FormItem, Input, Stack, Typography } from '@/components';
import AppUser from '@/entities/AppUser';
import { auth } from '@/firebase/FirebaseProvider';
import { createRemoteUser } from '@/firebase/RemoteUser';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { useState } from 'react';
import styles from './AuthPage.module.css';

interface SignUpProps {
  switchToSignIn: () => void;
}

const SignUp = ({ switchToSignIn }: SignUpProps): JSX.Element => {
  const [loading, setLoading] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');

  const signUpUser = async () => {
    setLoading(true);
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      const appUser: AppUser = {
        firstName: firstName,
        lastName: lastName,
        firestoreUserId: auth.currentUser?.uid,
      };
      await createRemoteUser(appUser);
    } catch (error) {
      alert(error);
    }
    setLoading(false);
  };

  return (
    <Stack className={styles.form} alignItems={'stretch'} spacing={3}>
      <Box>
        <Typography variant="titleLarge">Sign up</Typography>
      </Box>
      <FormItem>
        <Typography variant="labelSmall">First name</Typography>
        <Input
          onChange={(event) => {
            setFirstName(event.target.value);
          }}
        />
      </FormItem>
      <FormItem>
        <Typography variant="labelSmall">Last name</Typography>
        <Input
          onChange={(event) => {
            setLastName(event.target.value);
          }}
        />
      </FormItem>
      <FormItem>
        <Typography variant="labelSmall">Email</Typography>
        <Input
          placeholder="user@company.com"
          type={'email'}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
        />
      </FormItem>
      <FormItem>
        <Typography variant="labelSmall">Password</Typography>
        <Input
          type={'password'}
          onChange={(event) => {
            setPassword(event.target.value);
          }}
        />
      </FormItem>
      <Stack spacing={2}>
        <Button label={'Sign up'} disabled={loading} onClick={signUpUser} />
        <Button
          variant="soft"
          label={'Log in'}
          disabled={loading}
          onClick={switchToSignIn}
        />
      </Stack>
    </Stack>
  );
};

interface SignInProps {
  switchToSignUp: () => void;
}

const SignIn = ({ switchToSignUp }: SignInProps): JSX.Element => {
  const [loading, setLoading] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');

  return (
    <Stack className={styles.form} alignItems={'stretch'} spacing={3}>
      <Box>
        <Typography variant="titleLarge">Welcome on Flatten</Typography>
      </Box>
      <FormItem>
        <Typography variant="labelSmall">Email</Typography>
        <Input
          placeholder="user@company.com"
          type={'email'}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
        />
      </FormItem>
      <FormItem>
        <Typography variant="labelSmall">Password</Typography>
        <Input
          type={'password'}
          onChange={(event) => {
            setPassword(event.target.value);
          }}
        />
      </FormItem>
      <Stack spacing={2}>
        <Button
          label={'Sign in'}
          disabled={loading}
          onClick={async () => {
            setLoading(true);
            try {
              await signInWithEmailAndPassword(auth, email, password);
            } catch (error) {
              alert(error);
            }
            setLoading(false);
          }}
        />
        <Button
          variant="soft"
          label={'Create an account'}
          disabled={loading}
          onClick={switchToSignUp}
        />
      </Stack>
    </Stack>
  );
};

enum AuthPageMode {
  SIGN_IN = 'SIGN_IN',
  SIGN_UP = 'SIGN_UP',
}

const AuthPage = (): JSX.Element => {
  const [mode, setMode] = useState<AuthPageMode>(AuthPageMode.SIGN_IN);

  return (
    <div className={styles.container}>
      {mode === AuthPageMode.SIGN_IN && (
        <SignIn switchToSignUp={() => setMode(AuthPageMode.SIGN_UP)} />
      )}
      {mode === AuthPageMode.SIGN_UP && (
        <SignUp switchToSignIn={() => setMode(AuthPageMode.SIGN_IN)} />
      )}
    </div>
  );
};

export default AuthPage;
